import React, { useCallback, useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import StylePicker from './StylePicker';
import './AutoUploadSettings.css';

const STATUS_LABELS = {
    auto_uploaded: 'Uploaded to Strava',
    auto_uploaded_pending: 'Generating map...',
    claim_pending: 'Generating after your click...',
    claim_completed: 'Uploaded to Strava',
    link_appended: 'Link added to activity',
    skipped_already_generated: 'Skipped — already generated',
    skipped_public_activity: 'Skipped — activity was public',
    skipped_daily_cap: 'Skipped — daily limit reached',
    skipped_not_opted_in: 'Skipped — not opted in at the time',
    skipped_missing_scope: 'Skipped — Strava scope missing',
    skipped_no_token: 'Skipped — Strava reconnect needed',
    failed: 'Failed',
};

const AutoUploadSettings = ({ user }) => {
    const [enabled, setEnabled] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [followerThreshold, setFollowerThreshold] = useState(50);
    const [followerCount, setFollowerCount] = useState(null);
    const [recentEvents, setRecentEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const loadState = useCallback(async () => {
        if (!user) return;
        setIsLoading(true);
        try {
            const userDocRef = doc(db, 'users', user.uid);
            const userSnap = await getDoc(userDocRef);
            const userData = userSnap.data() || {};
            const auto = userData.settings?.autoUpload || {};
            setEnabled(auto.enabled === true);
            setSelectedTemplateId(auto.defaultPromptTemplateId || null);

            const tokenDocRef = doc(db, 'users', user.uid, 'stravaTokens', 'tokens');
            const tokenSnap = await getDoc(tokenDocRef);
            const followerCountValue = tokenSnap.data()?.athleteProfile?.follower_count;
            setFollowerCount(typeof followerCountValue === 'number' ? followerCountValue : null);

            const settingsSnap = await getDoc(doc(db, 'settings', 'mapGeneration'));
            const settingsData = settingsSnap.data() || {};
            setFollowerThreshold(settingsData.autoUploadFollowerThreshold ?? 50);

            const eventsQuery = query(
                collection(db, 'users', user.uid, 'autoUploadEvents'),
                orderBy('updatedAt', 'desc'),
                limit(5)
            );
            const eventsSnap = await getDocs(eventsQuery);
            setRecentEvents(eventsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (error) {
            console.error('Error loading auto-upload settings:', error);
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadState();
    }, [loadState]);

    const persistEnabled = async (nextEnabled) => {
        if (!user) return;
        if (nextEnabled && !selectedTemplateId) {
            alert('Pick a default style before turning auto-upload on.');
            return;
        }
        setIsSaving(true);
        try {
            await updateDoc(doc(db, 'users', user.uid), {
                'settings.autoUpload.enabled': nextEnabled,
            });
            setEnabled(nextEnabled);
        } catch (error) {
            console.error('Error updating auto-upload enabled:', error);
            alert('Failed to update setting. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const persistTemplate = async (template) => {
        if (!user) return;
        setIsSaving(true);
        try {
            await updateDoc(doc(db, 'users', user.uid), {
                'settings.autoUpload.defaultPromptTemplateId': template.id,
            });
            setSelectedTemplateId(template.id);
        } catch (error) {
            console.error('Error updating auto-upload style:', error);
            alert('Failed to update default style. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const isHighFollower = typeof followerCount === 'number' && followerCount >= followerThreshold;
    const flowLabel = typeof followerCount !== 'number'
        ? 'We\'ll check your follower count on first use'
        : isHighFollower
            ? `Auto-upload runs with no clicks (${followerCount} followers \u2265 ${followerThreshold})`
            : `You'll get a one-click confirm link on each activity (${followerCount} followers < ${followerThreshold})`;

    return (
        <div className="auto-upload-settings" id="auto-upload">
            <div className="auto-upload-row">
                <div className="auto-upload-info">
                    <h4>Auto-upload Mythic maps to Strava</h4>
                    <p>
                        {"We'll watch for new Strava activities, make a map in your chosen style, and add it to the activity automatically. You can turn this off any time."}
                    </p>
                    <p className="auto-upload-note">{"Auto-uploaded maps are free \u2013 they don't use credits."}</p>
                    <p className="auto-upload-note">{"Auto-upload only works for activities marked \u201cOnly Me\u201d (private) today."}</p>
                </div>
                <div className="auto-upload-control">
                    {isLoading ? (
                        <div className="loading-spinner-small" />
                    ) : (
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={enabled}
                                onChange={(e) => persistEnabled(e.target.checked)}
                                disabled={isSaving}
                            />
                            <span className="toggle-slider" />
                        </label>
                    )}
                </div>
            </div>

            <div className="auto-upload-status">
                <div className={`auto-upload-badge ${isHighFollower ? 'auto-upload-badge-direct' : 'auto-upload-badge-claim'}`}>
                    {flowLabel}
                </div>
            </div>

            <div className="auto-upload-style">
                <StylePicker
                    heading="Default style for auto-uploaded maps"
                    selectedTemplateId={selectedTemplateId}
                    onSelect={persistTemplate}
                    disabled={isSaving}
                />
            </div>

            {recentEvents.length > 0 && (
                <div className="auto-upload-events">
                    <h4>Recent auto-upload activity</h4>
                    <ul>
                        {recentEvents.map(ev => (
                            <li key={ev.id}>
                                <span className="auto-upload-event-activity">Activity {ev.activityId}</span>
                                <span className="auto-upload-event-status">{STATUS_LABELS[ev.status] || ev.status}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default AutoUploadSettings;
